import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AccountProvider } from './contexts/AccountContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SetupMFA from './pages/SetupMFA';
import Dashboard from './pages/Dashboard';
import StockScoring from './pages/StockScoring';
import VolatilityRanges from './pages/VolatilityRanges';
import MLPredictions from './pages/MLPredictions';
import TradingSignals from './pages/TradingSignals';
import PatternMatching from './pages/PatternMatching';
import TopMovers from './pages/TopMovers';
import TradingDashboard from './pages/TradingDashboard';
import MarketPosition from './pages/MarketPosition';
import Recommendations from './pages/Recommendations';
import Backtest from './pages/Backtest';
import DailyTasks from './pages/DailyTasks';
import NewsSignals from './pages/NewsSignals';
import NewsMovers from './pages/NewsMovers';
import MediaTrending from './pages/MediaTrending';
import FutureStocks from './pages/FutureStocks';
import FutureStocksLab from './pages/FutureStocksLab';
import AdvancedTrading from './pages/AdvancedTrading';
import IsraeliStartups from './pages/IsraeliStartups';
import IsraeliNews from './pages/IsraeliNews';
import TopPicks from './pages/TopPicks';
import Options from './pages/Options';
import Simulation from './pages/Simulation';
import Scoreboard from './pages/Scoreboard';
import SignalLab from './pages/SignalLab';
import PortfolioRisk from './pages/PortfolioRisk';
import CallTracker from './pages/CallTracker';
import Evidence from './pages/Evidence';
import CatalystChains from './pages/CatalystChains';
import Earnings from './pages/Earnings';
import Health from './pages/Health';
import MarketValuation from './pages/MarketValuation';
import TradeJournal from './pages/TradeJournal';
import Admin from './pages/Admin';
import ChangePassword from './pages/ChangePassword';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { logout, setProfile } from './store/slices/authSlice';

const App: React.FC = () => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isAdmin = useAppSelector((state) => state.auth.isAdmin);
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);
  const dispatch = useAppDispatch();
  const [tokenChecked, setTokenChecked] = useState(false);

  // Validate stored token against the server on every app load.
  // If the server doesn't recognise it (e.g. after a restart), clear it so the
  // user is sent to login/signup instead of hitting confusing errors.
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setTokenChecked(true);
      return;
    }
    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
    fetch(`${apiUrl}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 404) { dispatch(logout()); return; }
        if (res.ok) {
          const me = await res.json();
          dispatch(setProfile({ isAdmin: !!me.is_admin, mustChangePassword: !!me.must_change_password }));
        }
      })
      .catch(() => { /* network down — keep token, don't force logout */ })
      .finally(() => setTokenChecked(true));
    // Re-runs after login (isAuthenticated flips true) so admin status / forced
    // password-change are fetched immediately, not only on a full page reload.
  }, [dispatch, isAuthenticated]);

  if (!tokenChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AccountProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/signup" element={isAuthenticated ? <Navigate to="/" replace /> : <Signup />} />

        {/* MFA setup — public route, component guards itself via localStorage token */}
        <Route path="/setup-mfa" element={<SetupMFA />} />

        {/* Protected routes - Require authentication */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {mustChangePassword ? (
                <ChangePassword />
              ) : (
              <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/scoring" element={<StockScoring />} />
                    <Route path="/ranges" element={<VolatilityRanges />} />
                    <Route path="/predictions" element={<MLPredictions />} />
                    <Route path="/signals" element={<TradingSignals />} />
                    <Route path="/patterns" element={<PatternMatching />} />
                    <Route path="/movers" element={<TopMovers />} />
                    <Route path="/position" element={<MarketPosition />} />
                    <Route path="/recommendations" element={<Recommendations />} />
                    <Route path="/trading" element={<TradingDashboard />} />
                    <Route path="/backtest" element={<Backtest />} />
                    <Route path="/daily-tasks" element={<DailyTasks />} />
                    <Route path="/news-signals" element={<NewsSignals />} />
                    <Route path="/news-movers" element={<NewsMovers />} />
                    <Route path="/media-trending" element={<MediaTrending />} />
                    <Route path="/top-picks" element={<TopPicks />} />
                    <Route path="/options" element={<Options />} />
                    <Route path="/simulation" element={<Simulation />} />
                    <Route path="/scoreboard" element={<Scoreboard />} />
                    <Route path="/signal-lab" element={<SignalLab />} />
                    <Route path="/portfolio-risk" element={<PortfolioRisk />} />
                    <Route path="/calls" element={<CallTracker />} />
                    <Route path="/evidence" element={<Evidence />} />
                    <Route path="/catalyst-chains" element={<CatalystChains />} />
                    <Route path="/earnings" element={<Earnings />} />
                    <Route path="/health" element={<Health />} />
                    <Route path="/future-stocks" element={<FutureStocks />} />
                    <Route path="/future-stocks-lab" element={<FutureStocksLab />} />
                    <Route path="/advanced" element={<AdvancedTrading />} />
                    <Route path="/israeli-startups" element={<IsraeliStartups />} />
                    <Route path="/israeli-news" element={<IsraeliNews />} />
                    <Route path="/market-valuation" element={<MarketValuation />} />
                    <Route path="/trade-journal" element={<TradeJournal />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/admin" element={isAdmin ? <Admin /> : <Navigate to="/" replace />} />
                    {/* TODO: Add more routes when ready */}
                    {/* <Route path="/settings" element={<Settings />} /> */}
                  </Routes>
                </Layout>
              </Box>
              )}
            </ProtectedRoute>
          }
        />
      </Routes>
    </AccountProvider>
  );
};

export default App;
